import { Component, DestroyRef, Inject, OnInit, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ManagementService } from '../../management.service';
import { ResearchMemory, ResearchSnapshot } from '../../types';
import { MarkdownComponent } from '../../../../shared/components/markdown/markdown.component';
import { LoaderComponent } from '../../../../shared/components/loader/loader.component';

export interface ResearchViewerData {
  brandKey: string;
  brandName: string;
  snapshots: ResearchSnapshot[];
}

/**
 * Read-only research reader, opened as a dialog stacked over the brand drawer (DEV-522): country
 * tabs + the brand-memory markdown. Kept out of the drawer on purpose — the drawer stays one row.
 * The memory API is channel-free (one MD per country; channel-combined once LinkedIn lands), so
 * tabs are countries only.
 */
@Component({
  selector: 'app-research-viewer',
  imports: [CommonModule, MarkdownComponent, LoaderComponent],
  templateUrl: './research-viewer.component.html',
  styleUrl: './research-viewer.component.scss',
})
export class ResearchViewerComponent implements OnInit {
  public readonly countries: string[];
  public readonly selectedCountry = signal<string | null>(null);
  public readonly memory = signal<ResearchMemory | null>(null);
  public readonly loading = signal(false);
  public readonly error = signal<string | null>(null);

  // Meta line for the selected country: newest validity/research dates across its channels.
  public readonly selectedMeta = computed<{ validFrom: string | null; researchedAt: string | null }>(() => {
    const country = this.selectedCountry();
    const snapshots = this.data.snapshots.filter(snapshot => snapshot.country === country);
    const validFroms = snapshots.map(snapshot => snapshot.validFrom).sort();
    const researchedAts = snapshots.map(snapshot => snapshot.researchedAt).filter((date): date is string => !!date).sort();
    return { validFrom: validFroms.at(-1) ?? null, researchedAt: researchedAts.at(-1) ?? null };
  });

  private readonly api = inject(ManagementService);
  private readonly destroyRef = inject(DestroyRef);

  constructor(
    @Inject(MAT_DIALOG_DATA) public readonly data: ResearchViewerData,
    private readonly dialogRef: MatDialogRef<ResearchViewerComponent>
  ) {
    this.countries = [...new Set(data.snapshots.map(snapshot => snapshot.country))].sort();
  }

  ngOnInit(): void {
    if (this.countries.length) {
      this.select(this.countries[0]);
    } else {
      this.error.set('This brand has no research yet.');
    }
  }

  public select(country: string): void {
    if (this.selectedCountry() === country && this.memory()) return;
    this.selectedCountry.set(country);
    this.memory.set(null);
    this.error.set(null);
    this.loading.set(true);
    this.api.getBrandResearchMemory(this.data.brandKey, country)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: memory => {
          // Ignore a stale response if the user already switched tabs.
          if (this.selectedCountry() !== country) return;
          this.memory.set(memory);
          this.loading.set(false);
        },
        error: error => {
          if (this.selectedCountry() !== country) return;
          this.loading.set(false);
          // 404 is an expected state (researched brand, not this country yet); anything else gets a
          // generic line — the server already logs the real cause and never sends internals.
          this.error.set(error?.status === 404
            ? 'No research file for this country yet — it appears after the first completed run.'
            : 'Couldn\'t load the research file.');
        },
      });
  }

  public retry(): void {
    const country = this.selectedCountry();
    if (country) this.select(country); // memory is null in the error state, so select() reloads
  }

  public close(): void {
    this.dialogRef.close();
  }
}
